import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";

import { t } from "../contract/strings";
import { captureAnalytics } from "../analytics";
import { agreeToAiUse, aiConsentShown, aiConsentVersion } from "../engine/ai-consent";
import { newChirpContext } from "../engine/session";
import { signOutAndClearDevice } from "../engine/signin";
import { useApp } from "../state/store";
import { Button, DetailsPanel, LockIcon, MicIcon, ScreenHeading, StatusLine } from "../ui";
import { SparkleIcon } from "../ui/icons";
import { Screen } from "./screen";

/** Everything Tab can land on inside the sheet. */
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * "How MeetFolks uses AI" (#915), over whatever screen is up, until the person agrees.
 *
 * Mounted once in the shell, above every route, because it answers for the account rather
 * than for a screen: it shows on load or straight after sign-in, before Home's start and
 * join are usable, and again when the server refuses a start or join for it. See
 * `aiConsentShown` for exactly when.
 */
export function AiConsentGate() {
  const { pathname } = useLocation();
  const onConsentPage = pathname === "/consent";
  const shown = useApp((s) => aiConsentShown(s, pathname));
  const announced = useRef(false);
  useEffect(() => {
    // `/consent` is the route observer's screen view: the gate must not count it a second
    // time (#1085). It is left marked so navigating away from it while the sheet still
    // blocks does not count the same sheet again.
    if (onConsentPage) {
      announced.current = shown;
      return;
    }
    if (shown && !announced.current) {
      announced.current = true;
      // Consent is a screen of its own (#1085), even though it is drawn over the page.
      captureAnalytics("screen_viewed", { screen: "consent" });
    } else if (!shown) {
      announced.current = false;
    }
  }, [shown, onConsentPage]);
  if (onConsentPage) return null;
  return shown ? <AiConsentSheet /> : null;
}

/**
 * The consent screen on its own address (`/consent`): the same full-screen sheet the gate
 * shows, as its own registry screen (#1084 decision 2, #1085).
 */
export function ConsentPage() {
  return (
    <Screen name="consent">
      <AiConsentSheet />
    </Screen>
  );
}

/**
 * The sheet itself: the app's full-screen plate, like `Overlay`, but with no way out except
 * the two the ticket allows -- Agree and continue, or Sign out. No close button, no Escape,
 * no tap outside (there is no outside: it covers the screen).
 */
export function AiConsentSheet() {
  const navigate = useNavigate();
  const titleId = useId();
  const sheet = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Escape belongs to nobody while this is up: a code overlay under it would otherwise
  // close on a key the person pressed here. Caught at the window before anything else.
  useEffect(() => {
    const swallow = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", swallow, true);
    return () => window.removeEventListener("keydown", swallow, true);
  }, []);

  // The rest of the page is inert while the sheet is up -- the static header, footer and
  // theme toggle as well as the app -- so neither Tab nor a screen reader can reach behind
  // it. Focus starts on the sheet, which names itself, and goes back where it was after.
  useEffect(() => {
    const node = sheet.current;
    const root = node?.parentElement === document.body ? node : null;
    const made: Element[] = [];
    for (const child of Array.from(document.body.children)) {
      if (child === root || child.hasAttribute("inert")) continue;
      child.setAttribute("inert", "");
      made.push(child);
    }
    const before = document.activeElement as HTMLElement | null;
    node?.focus();
    return () => {
      for (const child of made) child.removeAttribute("inert");
      before?.focus?.();
    };
  }, []);

  /** Tab and Shift+Tab wrap inside the sheet. */
  function trapTab(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab" || !sheet.current) return;
    const items = Array.from(sheet.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === sheet.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  async function agree() {
    if (busy) return;
    const { aiConsentDemand: demand } = useApp.getState();
    // This click is the user activation a retried start's chirp needs (see
    // `newChirpContext`); the request below outlasts it.
    const chirpCtx = demand?.retry && demand.chirp ? newChirpContext() : null;
    setBusy(true);
    setFailed(false);
    const outcome = await agreeToAiUse(aiConsentVersion(useApp.getState()));
    setBusy(false);
    if (outcome === "agreed") {
      captureAnalytics("ai_consent_agreed", {});
      useApp.setState({ aiConsentDemand: null });
      if (demand?.retry) demand.retry(chirpCtx);
      return;
    }
    void chirpCtx?.close().catch(() => {});
    // A stale version has already put the current wording in the store: the sheet stays,
    // naming it, for the person to read and agree to.
    if (outcome === "failed") setFailed(true);
  }

  async function signOut() {
    // The same sign-out as Account's: it clears this device's voice profile, so it asks.
    if (!window.confirm(t("account.sign_out_confirm"))) return;
    useApp.setState({ aiConsentDemand: null });
    await signOutAndClearDevice();
    captureAnalytics("account_signed_out", {});
    navigate("/signin");
  }

  const link = (href: string, label: string) => {
    const which = href.startsWith("/privacy") ? "privacy" : href.startsWith("/terms") ? "terms" : null;
    return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        if (which) captureAnalytics("link_opened", { link: which });
      }}
      className="text-ink font-medium underline underline-offset-3 hover:text-accent"
    >
      {label}
    </a>
    );
  };

  return createPortal(
    <div
      ref={sheet}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={trapTab}
      data-ai-consent
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto ruled-ground pt-safe-t pb-safe-b animate-screen-in motion-reduce:animate-none outline-none"
    >
      <div className="w-full max-w-content mx-auto px-pad my-auto py-8">
        <ScreenHeading id={titleId} className="normal-case mt-0">
          {t("consent.title")}
        </ScreenHeading>

        <ul className="list-none p-0 m-0 grid gap-2">
          <Service icon={<MicIcon className="size-icon-xs flex-none mt-nudge text-accent" />}>
            {t("consent.stt")}
          </Service>
          <Service icon={<SparkleIcon className="size-icon-xs flex-none mt-nudge text-accent" />}>
            {t("consent.llm")}
          </Service>
        </ul>

        <h3>{t("consent.what_shared")}</h3><p>{t("consent.shared_detail")}</p>
        <h3>{t("consent.how_used")}</h3><p>{t("consent.used_detail")}</p>
        <DetailsPanel summary={t("consent.processing")}><p>{t("consent.routing_detail")}</p></DetailsPanel>

        <div className="flex items-start gap-2 my-gutter p-gutter rounded-control bg-surface-2 border border-line">
          <LockIcon className="size-icon-xs flex-none mt-nudge text-ink-soft" />
          <p className="m-0 text-note text-ink-soft">{t("consent.voice_profile_device")}</p>
        </div>

        <p className="mt-4 mx-0 mb-4 text-note text-ink-soft">
          {withLinks(t("consent.agree_terms_click"), {
            privacy: link("/privacy/", t("consent.privacy_link")),
            terms: link("/terms/", t("consent.terms_link")),
          })}
        </p>

        <Button onClick={() => void agree()} disabled={busy} aria-busy={busy}>
          {t("consent.agree")}
        </Button>
        <StatusLine kind={failed ? "err" : ""}>{failed ? t("consent.agree_failed") : ""}</StatusLine>
        <div className="flex justify-center mt-2">
          <Button variant="link" onClick={() => void signOut()} disabled={busy}>
            {t("account.sign_out")}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** One AI service and what it does, as a quiet row. */
function Service({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2 p-gutter rounded-control bg-surface border border-line">
      {icon}
      <span className="text-note text-ink">{children}</span>
    </li>
  );
}

/**
 * `text` with each `{name}` replaced by the node in `parts`: a sentence whose links are
 * words inside it, kept as one translatable string rather than three fragments.
 */
export function withLinks(text: string, parts: Record<string, ReactNode>): ReactNode[] {
  return text.split(/(\{[a-z_]+\})/).map((piece, index) => {
    const name = /^\{([a-z_]+)\}$/.exec(piece)?.[1];
    return name && name in parts ? <span key={index}>{parts[name]}</span> : piece;
  });
}
